import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useProfiles } from '@jwp/ott-hooks-react/src/useProfiles';
import ErrorPage from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import RootErrorPage from '@jwp/ott-ui-react/src/components/RootErrorPage/RootErrorPage';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';
import About from '@jwp/ott-ui-react/src/pages/About/About';
import ActivateTV from '@jwp/ott-ui-react/src/pages/ActivateTV/ActivateTV';
// import AppleFile from '@jwp/ott-ui-react/src/pages/AppleFile/AppleFile';
import Home from '@jwp/ott-ui-react/src/pages/Home/Home';
import Search from '@jwp/ott-ui-react/src/pages/Search/Search';
import User from '@jwp/ott-ui-react/src/pages/User/User';
import FavouritesPage from '@jwp/ott-ui-react/src/pages/FavouritesPage/FavouritesPage';
import NotificationsPage from '@jwp/ott-ui-react/src/pages/NotificationsPage/NotificationsPage';
import SeeMoreItemsPage from '@jwp/ott-ui-react/src/pages/SeeMoreItemsPage/SeeMoreItemsPage';
import LegacySeries from '@jwp/ott-ui-react/src/pages/LegacySeries/LegacySeries';
import MyVideosPage from '@jwp/ott-ui-react/src/pages/MyVideosPage/MyVideosPage';
import MediaScreenRouter from '@jwp/ott-ui-react/src/pages/ScreenRouting/MediaScreenRouter';
import PlaylistScreenRouter from '@jwp/ott-ui-react/src/pages/ScreenRouting/PlaylistScreenRouter';
import Layout from '@jwp/ott-ui-react/src/containers/Layout/Layout';
import Profiles from '@jwp/ott-ui-react/src/containers/Profiles/Profiles';
import CreateProfile from '@jwp/ott-ui-react/src/containers/Profiles/CreateProfile';
import EditProfile from '@jwp/ott-ui-react/src/containers/Profiles/EditProfile';
import VideoPreviewPage from '@jwp/ott-ui-react/src/pages/VideoPreviewPage/VideoPreviewPage';
import VideoPreviewSinglePage from '@jwp/ott-ui-react/src/pages/VideoPreviewPage/VideoPreviewSinglePage';
import MyVideoDetailsPage from '@jwp/ott-ui-react/src/pages/MyVideoDetailsPage/MyVideoDetailsPage';
import BroadcastLiveDetailPage from '@jwp/ott-ui-react/src/pages/BroadcastLiveDetailPage/BroadcastLiveDetailPage';
import BroadcastLivePlayPage from '@jwp/ott-ui-react/src/pages/BroadcastLivePlayPage/BroadcastLivePlayPage';
import ModerateContent from '@jwp/ott-ui-react/src/components/ModerateContent/ModerateContent';
import ReportsQueueList from '@jwp/ott-ui-react/src/components/ReportsQueue/ReportsQueueList';
import ReportsDetails from '@jwp/ott-ui-react/src/components/ReportDetails/ReportDetails';
import VerticalPlayer from '@jwp/ott-ui-react/src/components/VerticalPlayer/VerticalPlayer';
import SignInCognito from '@jwp/ott-ui-react/src/pages/SignInCognito/SignInCognito';
import SignUpCognito from '@jwp/ott-ui-react/src/pages/SignUpCognito/SignUpCognito';
import ForgotPassword from '@jwp/ott-ui-react/src/pages/ForgotPassword/ForgotPassword';
import ChannelPage from '@jwp/ott-ui-react/src/pages/ChannelPage/ChannelPage';
import AllSubscribersPage from '@jwp/ott-ui-react/src/pages/AllSubscribersPage/AllSubscribersPage';
import ResetPassword from '@jwp/ott-ui-react/src/pages/ResetPassword/ResetPassword';
import NewPassword from '@jwp/ott-ui-react/src/pages/ResetPassword/NewPassword';
import {
  PATH_ABOUT,
  PATH_LOGIN,
  PATH_SIGNUP,
  PATH_LEGACY_SERIES,
  PATH_MEDIA,
  PATH_PLAYLIST,
  PATH_USER_PROFILES,
  PATH_USER_PROFILES_CREATE,
  PATH_USER_PROFILES_EDIT,
  PATH_USER_PROFILES_EDIT_PROFILE,
  PATH_SEARCH,
  PATH_USER,
  RELATIVE_PATH_USER_FAVORITES,
  RELATIVE_PATH_USER_NOTIFICATIONS,
  PATH_USER_VIDEOS,
  RELATIVE_PATH_USER_SEEMORE_ITEMS,
  PATH_ACTIVATETV,
  RELATIVE_PATH_USER_TERMS_OF_USE,
  RELATIVE_PATH_USER_PRIVACY_POLICY,
  RELATIVE_PATH_CHECKOUT,
  PATH_VIDEO_PREVIEW,
  PATH_VIDEO_PREVIEW_WITH_ID,
  PATH_VIDEO_PREVIEW_CHANNEL,
  PATH_CHENNAL_PREVIEW,
  PATH_CHENNAL_PREVIEW_WITH_ID,
  PATH_MY_VIDEO_DETAILS_WITH_ID,
  PATH_BROADCAST_LIVE_DETAILS_WITH_ID,
  PATH_BROADCAST_LIVE_PLAY_WITH_ID,
  PATH_REPORTS_DETAILS_WITH_ID,
  PATH_USER_MODERATE,
  PATH_CHANNEL_REPORTS,
  PATH_REELS,
  PATH_RESET_PASSWORD,
  PATH_CHANGE_PASSWORD,
  PATH_FORGOT_PASSWORD,
  PATH_CHANNEL,
  PATH_CHANNEL_SUBSCRIBERS,
} from '@jwp/ott-common/src/paths';

import useNotificationEventSubscription from '@jwp/ott-ui-react/src/hooks/useNotificationEventSubscription';
import useNotifications from '#src/hooks/useNotifications';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import TermsOfUse from '@jwp/ott-ui-react/src/components/TermsOfUse/TermsOfUse';
import PrivacyPolicy from '@jwp/ott-ui-react/src/components/PrivacyPolicy/PrivacyPolicy';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import Toast from '@jwp/ott-ui-react/src/components/Toast/Toast';

import styles from './AppRoutes.module.scss';
import { CleengCheckout } from '@jwp/ott-ui-react/src/containers/Checkout/CleengCheckout';

export default function AppRoutes() {
  const location = useLocation();

  const { t } = useTranslation('error');
  const userModal = useQueryParam('u');

  const {
    isSwitchProfileEnable,
    isFavoriteEnable,
    isUploadEnabled,
    isActivateTV,
    isSearchEnabled,
    canShowPrivacyPolicy,
    canShowTermsOfUse,
    autoplayDefaultValue,
    isAutoPlay,
    authentication_type,
    isAuthenticationEnabled,
    isShowRegisterButton,
  } = useAppConfig();

  const { accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const user = useAccountStore(({ user }) => user, shallow);
  const { profile, selectingProfileAvatar } = useProfileStore();
  const { profilesEnabled } = useProfiles();

  useEffect(() => {
    if (!localStorage.getItem('autoplay') && isAutoPlay) {
      localStorage.setItem('autoplay', String(autoplayDefaultValue?.toLocaleLowerCase() == 'on'));
    }
  }, []);

  const userData = useAccountStore((s) => ({ loading: s.loading, user: s.user }));

  // Cleeng payment / account websocket notifications
  useNotifications();
  // In-app notification subscription → refetches getNotificationCount for bell dot
  useNotificationEventSubscription();

  if (userData.loading) {
    return <LoadingOverlay />;
  }

  if (userData.user && window.location.href.includes('#token')) {
    return <Navigate to="/" />; // component instead of hook to prevent extra re-renders
  }

  if (userData.user && selectingProfileAvatar !== null) {
    return <LoadingOverlay profileImageUrl={selectingProfileAvatar || profile?.avatar_url} />;
  }

  const shouldManageProfiles =
    !!user &&
    profilesEnabled &&
    !profile &&
    (accessModel === 'SVOD' || accessModel === 'AUTHVOD') &&
    !userModal &&
    isSwitchProfileEnable &&
    !location.pathname.includes(PATH_USER_PROFILES);

  if (shouldManageProfiles) {
    return <Navigate to={PATH_USER_PROFILES} />;
  }

  const canAccessApp = !!user || !isAuthenticationEnabled;

  const getPostAuthRedirect = () => {
    const from = (location.state as { from?: typeof location } | null)?.from;
    const fromPath = from ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}` : null;
    const redirectParam = new URLSearchParams(location.search).get('redirect');
    const candidate = fromPath || (redirectParam ? decodeURIComponent(redirectParam) : '/');

    if (!candidate.startsWith('/') || candidate.startsWith('//')) {
      return '/';
    }

    if (candidate === PATH_LOGIN || candidate.startsWith(`${PATH_LOGIN}?`) || candidate.startsWith(`${PATH_LOGIN}#`)) {
      return '/';
    }

    return candidate;
  };

  // Auth route elements — do not gate /login on can_show_signin_button (that flag only controls the header button).
  // Gating the route caused a / ↔ /login redirect loop when the button flag was false in QA.
  const loginElement = user ? <Navigate to={getPostAuthRedirect()} replace /> : <SignInCognito />;

  const getSignupElement = () => {
    if (user) { return <Navigate to="/" replace />; }
    if (!isShowRegisterButton) { return <Navigate to={PATH_LOGIN} replace />; }
    return <SignUpCognito />;
  };

  const forgotPasswordElement = user ? <Navigate to="/" replace /> : <ForgotPassword />;
  const resetPasswordElement = user ? <Navigate to="/" replace /> : <ResetPassword />;
  const changePasswordElement = user ? <Navigate to="/" replace /> : <NewPassword />;

  return (
    <>
      <Routes>
        {authentication_type === 'katon_cognito' && (
          <>
            <Route path={PATH_LOGIN} element={loginElement} />
            <Route path={PATH_SIGNUP} element={getSignupElement()} />
            <Route path={PATH_FORGOT_PASSWORD} element={forgotPasswordElement} />
            <Route path={PATH_RESET_PASSWORD} element={resetPasswordElement} />
            <Route path={PATH_CHANGE_PASSWORD} element={changePasswordElement} />
          </>
        )}
        <Route
          element={
            canAccessApp ? (
              <Outlet />
            ) : (
              <Navigate
                to={`${PATH_LOGIN}?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
                state={{ from: location }}
                replace
              />
            )
          }
        >
          <Route path={PATH_USER_PROFILES} element={<Profiles />} />
          <Route path={PATH_USER_PROFILES_CREATE} element={<CreateProfile />} />
          <Route path={PATH_USER_PROFILES_EDIT} element={<Profiles editMode />} />
          <Route path={PATH_USER_PROFILES_EDIT_PROFILE} element={<EditProfile />} />
          <Route path={PATH_VIDEO_PREVIEW} element={<VideoPreviewPage />} />
          <Route path={PATH_VIDEO_PREVIEW_WITH_ID} element={<VideoPreviewPage />} />
          <Route path={PATH_VIDEO_PREVIEW_CHANNEL} element={<VideoPreviewPage />} />
          <Route path={PATH_CHENNAL_PREVIEW} element={<VideoPreviewSinglePage />} />
          <Route path={PATH_CHENNAL_PREVIEW_WITH_ID} element={<VideoPreviewSinglePage />} />
          <Route path={PATH_REELS} element={<VerticalPlayer />} />
          <Route path={RELATIVE_PATH_CHECKOUT} element={<CleengCheckout />} />
          <Route element={<Layout />} errorElement={<RootErrorPage />}>
            <Route index element={<Home />} />
            <Route path={PATH_MEDIA} element={<MediaScreenRouter />} />
            <Route path={PATH_PLAYLIST} element={<PlaylistScreenRouter />} />
            <Route path={PATH_LEGACY_SERIES} element={<LegacySeries />} />
            {isSearchEnabled && <Route path={PATH_SEARCH} element={<Search />} />}
            {isFavoriteEnable && <Route path={RELATIVE_PATH_USER_FAVORITES} element={<FavouritesPage />} />}
            <Route path={RELATIVE_PATH_USER_NOTIFICATIONS} element={<NotificationsPage />} />
            {isUploadEnabled && hasPermission(user, PERMISSIONS.VIEW_MEDIA) && <Route path={PATH_USER_VIDEOS} element={<MyVideosPage />} />}
            <Route path={PATH_MY_VIDEO_DETAILS_WITH_ID} element={<MyVideoDetailsPage />} />
            <Route path={PATH_BROADCAST_LIVE_DETAILS_WITH_ID} element={<BroadcastLiveDetailPage />} />
            <Route path={PATH_BROADCAST_LIVE_PLAY_WITH_ID} element={<BroadcastLivePlayPage />} />
            <Route path="/channel/:handle" element={<ChannelPage />} />
            <Route path={PATH_CHANNEL_SUBSCRIBERS} element={<AllSubscribersPage />} />
            <Route path={PATH_CHANNEL} element={<ChannelPage />} />
            {isUploadEnabled && hasPermission(user, PERMISSIONS.APPROVALS_MEDIA) && <Route path={PATH_USER_MODERATE} element={<ModerateContent />} />}
            {hasPermission(user, PERMISSIONS.APPROVALS_CHANNEL_REPORT) && <Route path={PATH_CHANNEL_REPORTS} element={<ReportsQueueList />} />}
            <Route path={PATH_REPORTS_DETAILS_WITH_ID} element={<ReportsDetails />} />
            <Route path={RELATIVE_PATH_USER_SEEMORE_ITEMS} element={<SeeMoreItemsPage />} />
            <Route path={PATH_USER} element={<User />} />
            <Route path={PATH_ABOUT} element={<About />} />
            {isActivateTV && <Route path={PATH_ACTIVATETV} element={<ActivateTV />} />}
            {canShowTermsOfUse && <Route path={RELATIVE_PATH_USER_TERMS_OF_USE} element={<TermsOfUse panelClassName={styles.panel} />} />}
            {canShowPrivacyPolicy && <Route path={RELATIVE_PATH_USER_PRIVACY_POLICY} element={<PrivacyPolicy panelClassName={styles.panel} />} />}

            <Route
              path="/*"
              element={<ErrorPage title={t('notfound_error_heading', 'Not found')} message={t('notfound_error_description', "This page doesn't exist.")} />}
            />
          </Route>
        </Route>
        {/* <Route path={PATH_APPLE_FILE} element={<AppleFile />} /> */}
      </Routes>
      <Toast />
    </>
  );
}
